import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCorners,
  useSensor, useSensors, type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ApiError, api } from '../lib/api';
import { findElement, useBuilder } from '../lib/builderStore';
import { QUESTION_TYPES, typeMeta, type QuestionType, type ValidationIssue } from '../lib/types';
import { Palette, PalettePreview } from '../builder/Palette';
import { Canvas, ElementPreview } from '../builder/Canvas';
import { Properties } from '../builder/Properties';
import { Badge, Button, ErrorList, IconButton, Spinner } from '../components/ui';
import { Icon } from '../components/icons';

export function Builder() {
  const { formId = '' } = useParams();

  const definition = useBuilder((s) => s.definition);
  const loadedFormId = useBuilder((s) => s.formId);
  const dirty = useBuilder((s) => s.dirty);
  const undoStack = useBuilder((s) => s.undoStack);
  const redoStack = useBuilder((s) => s.redoStack);
  const load = useBuilder((s) => s.load);
  const undo = useBuilder((s) => s.undo);
  const redo = useBuilder((s) => s.redo);
  const markSaved = useBuilder((s) => s.markSaved);
  const addQuestion = useBuilder((s) => s.addQuestion);
  const addToGroup = useBuilder((s) => s.addToGroup);
  const moveElement = useBuilder((s) => s.moveElement);
  const moveIntoGroup = useBuilder((s) => s.moveIntoGroup);
  const placeBeside = useBuilder((s) => s.placeBeside);
  const setActivePage = useBuilder((s) => s.setActivePage);

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [issues, setIssues] = useState<{ errors: ValidationIssue[]; warnings: ValidationIssue[] }>(
    { errors: [], warnings: [] },
  );
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [dragging, setDragging] = useState<string | null>(null);

  const form = useQuery({
    queryKey: ['form', formId],
    queryFn: () => api.forms.get(formId),
    enabled: Boolean(formId),
  });

  const draft = useQuery({
    queryKey: ['definition', formId],
    queryFn: () => api.forms.getDefinition(formId),
    enabled: Boolean(formId),
  });

  useEffect(() => {
    if (draft.data && loadedFormId !== formId) load(formId, draft.data);
  }, [draft.data, formId, loadedFormId, load]);

  const save = useCallback(async () => {
    const current = useBuilder.getState().definition;
    if (!current || !formId) return;

    setSaveState('saving');
    try {
      const result = await api.forms.saveDefinition(formId, current);
      setIssues({ errors: result.errors, warnings: result.warnings });
      markSaved();
      setSaveState('saved');
    } catch {
      // The draft stays in memory, so a failed autosave is recoverable by retrying
      // rather than losing the author's work.
      setSaveState('error');
    }
  }, [formId, markSaved]);

  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!dirty) return;
    const timer = setTimeout(() => void saveRef.current(), 1500);
    return () => clearTimeout(timer);
  }, [dirty, definition]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;

      if (event.key === 'z' && !event.shiftKey) { event.preventDefault(); undo(); }
      else if ((event.key === 'z' && event.shiftKey) || event.key === 'y') {
        event.preventDefault(); redo();
      } else if (event.key === 's') { event.preventDefault(); void save(); }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, save]);

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const sensors = useSensors(
    // A small distance so a click to select is never mistaken for a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragStart(event: DragStartEvent) {
    setDragging(String(event.active.id));
  }

  /**
   * One handler for both gestures, because they share a drop target: dropping onto a page
   * appends, dropping onto a question inserts at its position. Dragging an existing
   * question across pages therefore works for free.
   */
  function handleDragEnd(event: DragEndEvent) {
    setDragging(null);

    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    const current = useBuilder.getState().definition;
    if (!current) return;

    // Dropped on the edge of another question: share that row rather than stack below it.
    // The widths are worked out from how many end up on the row, so a side-by-side layout
    // needs neither a columns block nor a visit to the width setting.
    if (overId.startsWith('side:')) {
      const [, side, targetRef] = overId.split(':');
      if (!side || !targetRef) return;

      // From the palette there is nothing to move yet, so the question is created first
      // and then placed — otherwise the drop would silently do nothing.
      if (activeId.startsWith('palette:')) {
        const type = activeId.slice(8) as QuestionType;
        const page = current.pages.find((p) => p.elements.some((e) => e.ref === targetRef));
        if (!page) return;

        addQuestion(type, page.ref, page.elements.findIndex((e) => e.ref === targetRef) + 1);

        // addQuestion selects what it made, which is how the new ref is known here.
        const added = useBuilder.getState().selectedRef;
        if (added) placeBeside(added, targetRef, side as 'left' | 'right');
        setAnnouncement(`${typeMeta(type)?.label ?? 'Question'} placed beside ${targetRef}.`);
        return;
      }

      placeBeside(activeId, targetRef, side as 'left' | 'right');
      setAnnouncement(`${activeId} moved beside ${targetRef}.`);
      return;
    }

    let targetPage: string | null = null;
    let targetIndex: number | undefined;
    let targetGroup: string | null = null;

    if (overId.startsWith('group:')) {
      // Dropping inside a layout group, which is the whole point of having one: this is
      // how a question ends up in a column rather than on the page underneath it.
      targetGroup = overId.slice(6);

      for (const page of current.pages) {
        if (page.elements.some((e) => e.ref === targetGroup)) { targetPage = page.ref; break; }
      }
    } else if (overId.startsWith('page:')) {
      targetPage = overId.slice(5);
    } else {
      for (const page of current.pages) {
        const at = page.elements.findIndex((e) => e.ref === overId);
        if (at >= 0) { targetPage = page.ref; targetIndex = at; break; }

        // Dropped onto a question that lives inside a group. The group is the destination —
        // resolving only the top level left this as "no target", so the drop did nothing.
        const owner = page.elements.find(
          (e) => e.kind === 'group' && (e.elements ?? []).some((c) => c.ref === overId));

        if (owner) {
          targetPage = page.ref;
          targetGroup = owner.ref;
          targetIndex = (owner.elements ?? []).findIndex((c) => c.ref === overId);
          break;
        }
      }
    }

    if (!targetPage) return;

    if (activeId.startsWith('palette:')) {
      const type = activeId.slice(8) as QuestionType;

      if (targetGroup) {
        // A container inside a container has no meaning here, and nesting one would give
        // the runtime a grid inside a grid cell with nothing deciding its width.
        if (type === 'columns') {
          setAnnouncement('A columns block cannot go inside another one.');
          return;
        }

        addToGroup(targetGroup, type);
        setAnnouncement(`${typeMeta(type)?.label ?? 'Question'} added to ${targetGroup}.`);
        return;
      }

      addQuestion(type, targetPage, targetIndex);
      setAnnouncement(`${typeMeta(type)?.label ?? 'Question'} added to ${targetPage}.`);
      return;
    }

    if (activeId === overId) return;

    if (targetGroup) {
      // Only the palette branch above used to read this, so dragging an existing question
      // onto a Columns block fell through to the page move and dropped it underneath the
      // group instead of inside it.
      const moving = current.pages
        .flatMap((p) => p.elements)
        .find((e) => e.ref === activeId);

      if (moving?.kind === 'group') {
        setAnnouncement('A columns block cannot go inside another one.');
        return;
      }

      moveIntoGroup(activeId, targetGroup, targetIndex);
      setAnnouncement(`${activeId} moved into ${targetGroup}.`);
      return;
    }

    moveElement(activeId, targetPage, targetIndex ?? Number.MAX_SAFE_INTEGER);
    setAnnouncement(`${activeId} moved to ${targetPage}.`);
  }

  async function publish() {
    setPublishing(true);
    setPublishError(null);

    try {
      if (dirty) await save();
      const result = await api.forms.publish(formId);

      setAnnouncement(result.unchanged
        ? 'No changes to publish.'
        : `Published version ${result.versionNumber}.`);
      setIssues((prev) => ({ ...prev, warnings: result.warnings ?? [] }));
      await form.refetch();
      await draft.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.isValidation) {
        setIssues((prev) => ({ ...prev, errors: err.issues }));
        setPublishError('Fix the problems below, then publish again.');
      } else {
        setPublishError(err instanceof ApiError ? err.message : 'Publish failed.');
      }
    } finally {
      setPublishing(false);
    }
  }

  const handleAdd = (type: QuestionType) => addQuestion(type);

  const quickAdd = (pageRef: string) => {
    setActivePage(pageRef);
    addQuestion('short_text', pageRef);
  };

  if (draft.isLoading || !definition) {
    return <div className="p-8"><Spinner label="Loading form" /></div>;
  }

  const dragPreview = renderDragPreview(dragging);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--ff-border)]
                      bg-[var(--ff-surface)] px-4 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold">{form.data?.name ?? definition.title}</span>
            {form.data && <StatusPill status={form.data.status} />}
          </div>
          <SaveIndicator state={saveState} dirty={dirty} />
        </div>

        <div className="ml-auto flex items-center gap-1">
          <IconButton label="Undo (Ctrl+Z)" onClick={undo} disabled={undoStack.length === 0}>
            <Icon name="undo" />
          </IconButton>
          <IconButton label="Redo (Ctrl+Shift+Z)" onClick={redo} disabled={redoStack.length === 0}>
            <Icon name="redo" />
          </IconButton>

          <span className="mx-1 h-5 w-px bg-[var(--ff-border)]" aria-hidden="true" />

          {form.data?.publishedVersion && (
            <a
              href={`/e?f=${formId}&o=${encodeURIComponent(location.origin)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm
                         text-[var(--ff-text-muted)] hover:bg-[var(--ff-surface-raised)]
                         hover:text-[var(--ff-text)]"
            >
              <Icon name="external" /> Preview
            </a>
          )}

          <Button variant="primary" onClick={publish} disabled={publishing}>
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
      </div>

      <div role="status" aria-live="polite" className="ff-sr">{announcement}</div>

      {(publishError || issues.errors.length > 0 || issues.warnings.length > 0) && (
        <div className="space-y-2 border-b border-[var(--ff-border)] bg-[var(--ff-surface)]
                        px-4 py-3">
          {publishError && (
            <p role="alert" className="flex items-center gap-1.5 text-sm font-medium
                                       text-[var(--ff-danger)]">
              <Icon name="alert" /> {publishError}
            </p>
          )}
          <ErrorList title="Publish is blocked by these problems" issues={issues.errors} />
          <ErrorList title="Worth checking before you publish" issues={issues.warnings}
                     tone="warning" />
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDragging(null)}
        accessibility={{
          announcements: {
            onDragStart: ({ active }) => `Picked up ${describe(String(active.id))}.`,
            onDragOver: ({ active, over }) => over
              ? `${describe(String(active.id))} is over ${describe(String(over.id))}.`
              : `${describe(String(active.id))} is not over a drop target.`,
            onDragEnd: ({ active, over }) => over
              ? `${describe(String(active.id))} dropped on ${describe(String(over.id))}.`
              : `${describe(String(active.id))} dropped.`,
            onDragCancel: ({ active }) => `Dragging ${describe(String(active.id))} cancelled.`,
          },
        }}
      >
        {/*
          Three columns that scroll independently.

          grid-rows-[minmax(0,1fr)] is what makes that true. A grid row is content-sized by
          default, so the row grew to whichever column was tallest and pushed the whole
          builder past its container — you scrolled the page to reach the bottom of the
          palette, and the drag you started there scrolled out from under you. Bounding the
          row to the container's height hands each column its own scrollbar instead.
        */}
        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)]
                        lg:grid-cols-[248px_minmax(0,1fr)_312px]">
          {/* overflow-hidden, not auto: the palette scrolls its own list below a search
              box that stays put. */}
          <aside className="hidden min-h-0 min-w-0 overflow-hidden border-r
                            border-[var(--ff-border)] lg:block"
                 aria-label="Question types">
            <Palette onAdd={handleAdd} />
          </aside>

          {/* min-w-0 keeps the wide canvas from pushing the properties panel off-screen. */}
          <div className="min-h-0 min-w-0 overflow-auto bg-[var(--ff-surface-sunken)]">
            <Canvas onQuickAdd={quickAdd} />
          </div>

          <aside className="hidden min-h-0 min-w-0 overflow-y-auto border-l
                            border-[var(--ff-border)] bg-[var(--ff-surface)] lg:block"
                 aria-label="Question settings">
            <Properties />
          </aside>
        </div>

        <DragOverlay dropAnimation={null}>{dragPreview}</DragOverlay>
      </DndContext>
    </div>
  );
}

function renderDragPreview(dragging: string | null) {
  if (!dragging) return null;

  if (dragging.startsWith('palette:')) {
    const item = QUESTION_TYPES.find((t) => t.type === dragging.slice(8));
    return item ? <PalettePreview item={item} /> : null;
  }

  const found = findElement(useBuilder.getState().definition, dragging);
  return found ? <ElementPreview element={found.element} /> : null;
}

function describe(id: string): string {
  if (id.startsWith('palette:')) {
    return typeMeta(id.slice(8))?.label ?? 'question type';
  }
  if (id.startsWith('page:')) return `page ${id.slice(5)}`;
  return id;
}

function SaveIndicator({
  state, dirty,
}: { state: 'idle' | 'saving' | 'saved' | 'error'; dirty: boolean }) {
  const text =
    state === 'saving' ? 'Saving…'
      : state === 'error' ? 'Could not save — your changes are still here, retrying on next edit'
        : dirty ? 'Unsaved changes'
          : state === 'saved' ? 'All changes saved'
            : 'Draft';

  return (
    <p className={`flex items-center gap-1 text-[11px] ${
      state === 'error' ? 'text-[var(--ff-danger)]' : 'text-[var(--ff-text-muted)]'
    }`}>
      {state === 'saved' && !dirty && <Icon name="check" size={12} />}
      {state === 'error' && <Icon name="alert" size={12} />}
      {text}
    </p>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone = status === 'Published' ? 'success' : status === 'Draft' ? 'neutral' : 'warning';
  return <Badge tone={tone}>{status}</Badge>;
}
